import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles } from 'lucide-react';
import type { Competition } from '../../data/competitions';

interface Props {
  comp: Competition;
  onGoTo: (tab: string) => void;
}

export default function OverviewTab({ comp, onGoTo }: Props) {
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-5 lg:col-span-2">
        {comp.image && (
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <img src={comp.image} alt="" className="h-48 w-full object-cover sm:h-64" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/10 to-transparent" />
            <div className="absolute bottom-3 left-4">
              <h2 className={`text-2xl font-extrabold ${comp.text}`}>{comp.arenaName}</h2>
            </div>
          </div>
        )}

        {comp.overview.map((p, i) => (
          <motion.p
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="leading-relaxed text-slate-300/90"
          >
            {p}
          </motion.p>
        ))}

        <div className="flex flex-wrap gap-3 pt-2">
          {(['Rules', 'Build', 'Simulate'] as const).map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => onGoTo(step)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold transition ${comp.button}`}
            >
              <Sparkles className="size-4" />
              {step === 'Rules' ? 'Read the rules' : step === 'Build' ? 'Build your robot' : 'Run a simulation'}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Arena features</h3>
          <ul className="mt-3 space-y-2.5">
            {comp.arenaFeatures.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle2 className={`mt-0.5 size-4 shrink-0 ${comp.text}`} />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">Skills you'll practice</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {comp.skills.map((s) => (
              <span key={s} className="rounded-md bg-white/5 px-2.5 py-1.5 text-xs font-medium text-slate-300">
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-400">At a glance</h3>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-400">Difficulty</dt>
              <dd className="font-semibold text-slate-200">{comp.difficulty}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-400">Build time</dt>
              <dd className="font-semibold text-slate-200">{comp.buildTime}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
